import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, CheckCircle, AlertCircle, Clock, Lock, Unlock, Settings, 
  Calendar, RotateCcw, Search, Eye, Bell, ListFilter, FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import ReportDetailsModal from './ReportDetailsModal';
import api from '../api';

export default function DailyReports() {
  const [activeTab, setActiveTab] = useState('Today'); // 'Today', 'History', 'Defaulters'
  const [selectedReport, setSelectedReport] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Today Data
  const [todayData, setTodayData] = useState({
    total: 0,
    counts: { submitted: 0, missed: 0, window_open: 0 },
    submitted: [],
    missed: [],
    window_open: [],
    is_window_override: false
  });
  
  // Window Control Data
  const [windowStatus, setWindowStatus] = useState(null);
  const [overrideMinutes, setOverrideMinutes] = useState('60');

  // History Data
  const [historyData, setHistoryData] = useState([]);
  
  // Defaulters Data
  const [defaultersData, setDefaultersData] = useState([]);

  // Filters for History
  const [filterDate, setFilterDate] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (activeTab === 'Today') {
      fetchTodayData();
      fetchWindowStatus();
    } else if (activeTab === 'History') {
      fetchHistoryData();
    } else if (activeTab === 'Defaulters') {
      fetchDefaultersData();
    }
  }, [activeTab, filterDate, filterMonth, filterStatus]);

  const fetchTodayData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/work-reports/today');
      setTodayData(res.data);
    } catch (err) {
      console.error("Error fetching today's reports:", err);
      setError("Failed to load today's overview.");
    } finally {
      setLoading(false);
    }
  };

  const fetchWindowStatus = async () => {
    try {
      const res = await api.get('/admin/work-reports/window-status');
      setWindowStatus(res.data);
    } catch (err) {
      console.error("Error fetching window status:", err);
    }
  };

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      let url = '/admin/work-reports?per_page=50&';
      if (filterDate) url += `date=${filterDate}&`;
      if (filterMonth) {
        const [y, m] = filterMonth.split('-');
        url += `month=${m}&year=${y}&`;
      }
      if (filterStatus !== 'all') url += `status=${filterStatus}&`;
      
      const res = await api.get(url);
      setHistoryData(res.data.data || []);
    } catch (err) {
      console.error("Error fetching history:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultersData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/work-reports/defaulters');
      setDefaultersData(res.data);
    } catch (err) {
      console.error("Error fetching defaulters:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenWindow = async () => {
    try {
      await api.post('/admin/work-reports/override-window', { close_minutes: parseInt(overrideMinutes) });
      fetchTodayData();
      fetchWindowStatus();
      toast.success(`Submission window opened for ${overrideMinutes} minutes!`);
    } catch (err) {
      console.error("Error opening window:", err);
      toast.error("Failed to open window.");
    }
  };

  const handleCloseWindow = async () => {
    try {
      await api.delete('/admin/work-reports/override-window');
      fetchTodayData();
      fetchWindowStatus();
      toast.success("Submission window restored to default.");
    } catch (err) {
      console.error("Error closing window:", err);
      toast.error("Failed to restore window.");
    }
  };

  const handleReview = async (reportId, comment) => {
    try {
      await api.post(`/admin/work-reports/${reportId}/review`, { manager_comment: comment });
      setSelectedReport(null);
      if (activeTab === 'Today') fetchTodayData();
      else if (activeTab === 'History') fetchHistoryData();
      toast.success('Report marked as reviewed successfully!');
    } catch (err) {
      console.error('Error reviewing report:', err);
      toast.error('Failed to mark as reviewed. Please try again.');
    }
  };

  const sendWarning = async (userId) => {
    try {
      await api.post(`/admin/work-reports/defaulters/${userId}/warn`);
      toast.success("Warning sent successfully!");
    } catch (err) {
      console.error("Error sending warning:", err);
      toast.error("Failed to send warning.");
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  return (
    <div className="space-y-3 animate-in fade-in duration-300">
      
      {/* Top Header & Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-3.5 flex flex-col lg:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-6 w-full lg:w-auto">
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center border border-emerald-100 shadow-sm">
              <ClipboardList size={22} strokeWidth={2.5} />
            </div>
            <h1 className="text-xl font-black text-slate-800 tracking-tight whitespace-nowrap">Daily Work Reports</h1>
          </div>

          {/* Inline Filters for History Mode - Synchronized Height */}
          {activeTab === 'History' && (
            <div className="hidden lg:flex items-center gap-3 pl-6 border-l border-slate-100 h-11">
              <div className="flex items-center gap-2 h-full">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Date</span>
                <input 
                  type="date" 
                  value={filterDate}
                  onChange={(e) => {setFilterDate(e.target.value); setFilterMonth('');}}
                  className="border border-slate-200 rounded-md text-[12px] font-bold text-slate-700 h-full px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                />
              </div>

              <div className="flex items-center gap-2 h-full">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Month</span>
                <input 
                  type="month" 
                  value={filterMonth}
                  onChange={(e) => {setFilterMonth(e.target.value); setFilterDate('');}}
                  className="border border-slate-200 rounded-md text-[12px] font-bold text-slate-700 h-full px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                />
              </div>

              <div className="flex items-center gap-2 h-full">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Status</span>
                <select 
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border border-slate-200 rounded-md text-[12px] font-bold text-slate-700 h-full px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-gray-50/50 cursor-pointer hover:bg-gray-100/50 transition-colors"
                >
                  <option value="all">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="reviewed">Reviewed</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Premium Segmented Control */}
        <div className="flex p-1 bg-slate-100/50 border border-slate-200/50 rounded-md shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] h-11 shrink-0">
          {['Today', 'History', 'Defaulters'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 h-full text-[13px] font-black rounded-md transition-all duration-200 whitespace-nowrap ${
                activeTab === tab 
                  ? 'bg-white text-indigo-600 shadow-md shadow-indigo-100/50 scale-[1.02] border border-white' 
                  : 'text-slate-500 hover:text-slate-800 hover:bg-white/40'
              } ${tab === 'Defaulters' && activeTab === tab ? 'text-red-600' : ''} ${tab === 'Defaulters' && activeTab !== tab ? 'text-red-400/80' : ''}`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="py-20 text-center font-bold text-slate-400">Loading {activeTab} Data...</div>}

      {/* TODAY TAB */}
      {!loading && activeTab === 'Today' && (
        <div className="space-y-2">
          
          {/* 4 Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl px-4 py-3 border border-gray-100 shadow-sm flex flex-col justify-center">
              <span className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-0.5">TOTAL EXPECTED</span>
              <div className="text-2xl font-black text-slate-800 leading-none">{todayData.total}</div>
              <p className="text-[10px] font-bold text-slate-400 mt-1 flex items-center gap-1"><Calendar size={12}/> Emp. Scheduled</p>
            </div>
            
            <div className="bg-white rounded-xl px-4 py-3 border border-emerald-100 shadow-sm flex flex-col justify-center">
              <span className="text-[10px] uppercase font-black tracking-widest text-emerald-600 flex items-center gap-1.5 mb-0.5"><CheckCircle size={12} strokeWidth={3}/> SUBMITTED</span>
              <div className="text-2xl font-black text-emerald-600 leading-none">{todayData.counts?.submitted || 0}</div>
            </div>

            <div className="bg-white rounded-xl px-4 py-3 border border-red-100 shadow-sm flex flex-col justify-center">
              <span className="text-[10px] uppercase font-black tracking-widest text-red-500 flex items-center gap-1.5 mb-0.5"><AlertCircle size={12} strokeWidth={3}/> MISSED</span>
              <div className="text-2xl font-black text-red-600 leading-none">{todayData.counts?.missed || 0}</div>
            </div>

            <div className="bg-[#fffdf0] rounded-xl px-4 py-3 border border-[#fde68a] shadow-sm flex flex-col justify-center">
              <span className="text-[10px] uppercase font-black tracking-widest text-[#d97706] flex items-center gap-1.5 mb-0.5"><Clock size={12} strokeWidth={3}/> WINDOW OPEN</span>
              <div className="text-2xl font-black text-[#b45309] leading-none">{todayData.counts?.window_open || 0}</div>
              <p className="text-[10px] font-bold text-[#d97706] mt-1 bg-[#fef3c7] self-start px-2 py-0.5 rounded">Inside window</p>
            </div>
          </div>

          {/* Submission Window Control */}
          <div className="bg-slate-50/50 rounded-xl px-4 py-3 border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border ${windowStatus?.is_currently_open ? 'bg-emerald-100/50 text-emerald-500 border-emerald-200' : 'bg-slate-200/50 text-slate-400 border-slate-300/50'}`}>
                {windowStatus?.is_currently_open ? <Unlock size={20} strokeWidth={2.5} /> : <Lock size={20} strokeWidth={2.5} />}
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-slate-800 flex items-center gap-2">
                  Submission Window: 
                  {windowStatus?.is_currently_open ? (
                    <span className="text-[11px] font-black uppercase text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded tracking-wider">OPEN</span>
                  ) : (
                    <span className="text-[11px] font-black uppercase text-slate-500 bg-slate-200 px-2 py-0.5 rounded tracking-wider">CLOSED</span>
                  )}
                  <span className="text-[13px] font-semibold text-slate-500">• Default: {windowStatus?.default_window || '5:00 PM - 8:00 PM'}</span>
                  {windowStatus?.is_override && (
                    <RotateCcw onClick={handleCloseWindow} size={14} className="text-red-400 cursor-pointer hover:text-red-600" title="Reset to default window" />
                  )}
                </h3>
                <p className="text-[12px] font-medium text-slate-500 mt-1">
                  Server time: <span className="font-bold text-slate-700">{windowStatus?.server_time || '--:--'}</span> · {windowStatus?.is_override ? `Closes at ${windowStatus?.window_close}` : 'Use the override to open submissions outside default hours.'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <select 
                value={overrideMinutes}
                onChange={(e) => setOverrideMinutes(e.target.value)}
                className="border border-slate-300 rounded-lg text-[13px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none shadow-sm cursor-pointer"
              >
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
                <option value="240">4 hours</option>
              </select>
              <button 
                onClick={handleOpenWindow}
                className="flex items-center gap-2 bg-[#10b981] hover:bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-[13px] font-bold transition-colors shadow-sm whitespace-nowrap"
              >
                <Unlock size={16} strokeWidth={2.5} /> Open Window Now
              </button>
            </div>
          </div>

          {/* Bottom Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            
            {/* Today's Submissions */}
            <div className="lg:col-span-2">
              <h3 className="text-[13px] font-black flex items-center gap-1.5 text-slate-700 mb-4 px-1">
                <CheckCircle size={16} className="text-emerald-500" /> Today's Submissions
              </h3>
              
              {todayData.submitted && todayData.submitted.length > 0 ? (
                <div className="bg-white border border-gray-100 rounded-xl shadow-sm h-[300px] overflow-y-auto px-4 py-3 space-y-2 custom-scrollbar">
                  {todayData.submitted.map((sub, idx) => (
                    <div key={idx} className="p-2.5 bg-white/60 hover:bg-white rounded-lg border border-gray-100 flex items-center justify-between cursor-default shadow-sm transition-all">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold shadow-sm uppercase text-[12px] tracking-tight">
                          {getInitials(sub.name)}
                        </div>
                        <div>
                          <h4 className="text-[13px] font-black text-slate-800">{sub.name}</h4>
                          <p className="text-[11px] font-bold text-slate-500 mt-0.5 truncate max-w-[200px]">{sub.report?.tasks_completed}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                         <span className="text-[10px] font-black uppercase text-emerald-600 bg-emerald-50 px-2 py-1 rounded shadow-sm tracking-wider">Submitted</span>
                         <button onClick={() => setSelectedReport({ ...sub.report, name: sub.name })} className="p-1 text-slate-400 hover:text-emerald-600"><Eye size={16}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm h-[300px] flex flex-col items-center justify-center text-slate-400">
                  <FileText size={48} className="mb-3 opacity-20" strokeWidth={1.5} />
                  <p className="text-[14px] font-semibold text-slate-500">No reports submitted yet today.</p>
                </div>
              )}
            </div>

            {/* Waiting (Window Open) */}
            <div>
              <h3 className="text-[13px] font-black flex items-center gap-1.5 text-slate-700 mb-4 px-1">
                <Clock size={16} className="text-[#d97706]" /> Waiting (Window Open)
              </h3>
              
              {todayData.window_open && todayData.window_open.length > 0 ? (
                <div className="bg-[#fffdf0] border border-[#fde68a] rounded-xl shadow-sm h-[300px] overflow-y-auto px-4 py-3 space-y-2 custom-scrollbar">
                  {todayData.window_open.map((item, idx) => (
                    <div key={idx} className="p-3 bg-white/60 hover:bg-white rounded-lg border border-[#fef3c7] transition-all flex items-center justify-between cursor-default">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#fef3c7] text-[#d97706] flex items-center justify-center font-bold shadow-sm uppercase">
                          {item.name.charAt(0)}
                        </div>
                        <div>
                          <h4 className="text-[13px] font-black text-slate-800">{item.name}</h4>
                          <p className="text-[11px] font-bold text-slate-500 mt-0.5">Deadline: {item.window_close}</p>
                        </div>
                      </div>
                      <span className="text-[10px] font-black uppercase text-[#d97706] bg-[#fef3c7] px-2 py-1 rounded shadow-sm tracking-wider">Waiting</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-100 rounded-xl shadow-sm h-[300px] flex flex-col items-center justify-center text-slate-400">
                  <span className="text-[12px] font-bold mb-2">Nobody is currently scheduled for submission.</span>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {!loading && activeTab === 'History' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-full min-h-[500px]">
          
          {/* Main Mobile Filters (Visible only on small screens) */}
          <div className="lg:hidden px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
             <div className="w-full">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Filter by Date</span>
                <input 
                  type="date" 
                  value={filterDate}
                  onChange={(e) => {setFilterDate(e.target.value); setFilterMonth('');}}
                  className="border border-slate-200 rounded-lg text-[13px] font-semibold text-slate-700 py-2 px-3 focus:outline-none shadow-sm w-full bg-gray-50"
                />
             </div>
             <div className="w-full">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Filter by Month</span>
                <input 
                  type="month" 
                  value={filterMonth}
                  onChange={(e) => {setFilterMonth(e.target.value); setFilterDate(''); }}
                  className="border border-slate-200 rounded-lg text-[13px] font-semibold text-slate-700 py-2 px-3 focus:outline-none shadow-sm w-full bg-gray-50"
                />
             </div>
          </div>

          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="px-6 py-3">EMPLOYEE</th>
                  <th className="px-4 py-3">DATE</th>
                  <th className="px-4 py-3">STATUS</th>
                  <th className="px-4 py-3 w-1/3">TASKS COMPLETED SNIPPET</th>
                  <th className="px-6 py-3 text-right">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 border-t border-slate-100">
                {historyData.length > 0 ? historyData.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-2.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-[11px] shadow-sm uppercase tracking-tight">
                          {getInitials(row.user?.name || 'E')}
                        </div>
                        <div>
                          <div className="text-[13px] font-black text-slate-800">{row.user?.name || 'Unknown User'}</div>
                          <div className="text-[11px] font-semibold text-slate-500 mt-0.5">{row.total_admissions} admissions reported</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-black text-slate-800">
                          {new Date(row.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                        {row.submitted_at && (
                          <span className="text-[10px] font-bold text-slate-400 bg-slate-50 inline-block px-1.5 py-0.5 rounded border border-slate-100 whitespace-nowrap">
                             {new Date(row.submitted_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {row.status === 'submitted' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-emerald-200 bg-white text-emerald-600 text-[11px] font-bold shadow-sm whitespace-nowrap">
                          <Clock size={12} strokeWidth={3} /> Submitted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] font-bold shadow-sm whitespace-nowrap">
                          <CheckCircle size={12} strokeWidth={3} /> Reviewed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="text-[13px] font-medium text-slate-600 truncate max-w-[300px]">{row.tasks_completed}</p>
                    </td>
                    <td className="px-6 py-2.5 text-right">
                      <button 
                        onClick={() => setSelectedReport(row)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-md bg-white hover:bg-slate-50 text-[11px] font-bold text-slate-600 transition-colors shadow-sm uppercase tracking-wider"
                      >
                        <Eye size={14} strokeWidth={2.5} /> View
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="text-center py-10 font-bold text-slate-400">No report history found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DEFAULTERS TAB */}
      {!loading && activeTab === 'Defaulters' && (
        <div className="bg-white rounded-xl shadow-sm border border-red-100 overflow-hidden min-h-[500px]">
          
          <div className="bg-red-50/50 px-6 py-5 border-b border-red-100 flex items-center justify-between">
            <h2 className="text-[15px] font-black text-red-600 flex items-center gap-2">
              <AlertCircle size={20} strokeWidth={2.5} /> Action Required: Missing Reports
            </h2>
            <p className="text-[12px] font-bold text-red-500">Employees who haven't submitted DWRs</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="px-6 py-3">EMPLOYEE</th>
                  <th className="px-4 py-3">DAYS MISSED</th>
                  <th className="px-4 py-3">LAST REPORT DATE</th>
                  <th className="px-6 py-3 text-right">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 border-t border-slate-100">
                {defaultersData.length > 0 ? defaultersData.map((defaulter, idx) => (
                  <tr key={idx} className="hover:bg-red-50/20 transition-colors">
                    <td className="px-6 py-2.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-red-100 text-red-500 flex items-center justify-center font-bold text-[11px] shadow-sm uppercase tracking-tight">
                          {getInitials(defaulter.name)}
                        </div>
                        <div className="text-[13px] font-black text-slate-800">{defaulter.name}</div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-block px-3 py-1 bg-red-100 text-red-600 text-[11px] font-black rounded-lg shadow-sm">
                        {defaulter.days_missed} Days
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-[13px] font-bold text-slate-500">{defaulter.last_report_formatted || 'Never Submitted'}</span>
                    </td>
                    <td className="px-6 py-2.5 text-right">
                      <button onClick={() => sendWarning(defaulter.user_id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-200 rounded-lg bg-white hover:bg-red-50 text-[11px] font-black text-red-500 transition-colors shadow-sm tracking-wide">
                        <Bell size={14} strokeWidth={2.5} /> Send Warning
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="text-center py-10 font-bold text-slate-400">No defaulters found! Excellent.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Render Modal if a report is selected */}
      <ReportDetailsModal 
        isOpen={!!selectedReport}
        onClose={() => setSelectedReport(null)}
        report={selectedReport}
        onReview={handleReview}
      />
    </div>
  );
}
